import { add, min } from 'date-fns';
import { DoneActorEvent, assign, emit, enqueueActions, fromPromise, setup } from 'xstate';

import { Timestamp } from '@flipster/apis/types';
import { postMfaInitiateApi, postMfaRequestApi, postMfaVerifyApi } from '@flipster/apis/v1/mfa';
import {
  MfaRequestChannel,
  MfaScope,
  MfaVerifyChannel,
  PostMfaInitiateParams,
  PostMfaInitiateResponse,
  PostMfaRequestParams,
  PostMfaRequestResponse,
  PostMfaVerifyParams,
  PostMfaVerifyResponse,
} from '@flipster/apis/v1/mfa/types';
import { AxiosError } from '@flipster/utils/axios';
import { convertDate } from '@flipster/utils/datetime';
import { getErrorCode } from '@flipster/utils/error';

export const initiateMfa = fromPromise<PostMfaInitiateResponse, PostMfaInitiateParams>(async ({ input }) => {
  const { data } = await postMfaInitiateApi(input);
  return data;
});

export const requestMfaCode = fromPromise<PostMfaRequestResponse, PostMfaRequestParams>(async ({ input }) => {
  const { data } = await postMfaRequestApi(input);
  return data;
});

export const verifyMfaCode = fromPromise<PostMfaVerifyResponse, PostMfaVerifyParams>(async ({ input }) => {
  const { data } = await postMfaVerifyApi(input);
  return data;
});

interface Channel {
  expiresAt?: Timestamp;
  verified?: boolean;
}

interface Context {
  mfaScope: MfaScope;
  passkeyAvailable: boolean;
  authToken: string;
  email?: string;
  maskedPhoneNumber?: string;
  passwordResetToken?: string;
  withdrawalRequestId?: string;
  requirePasskey: boolean;
  requireTotp: boolean;
  requireSms: boolean;
  requireEmail: boolean;
  mfaSessionToken: string;
  mfaSessionExpiresAt: Timestamp;
  needToVerify: ('passkey' | MfaVerifyChannel)[];
  isChannelLoading: boolean;
  channel: Record<MfaVerifyChannel, Channel | undefined>;
}

type EventType =
  | 'initiate'
  | 'email.request'
  | 'sms.request'
  | 'email.verify'
  | 'sms.verify'
  | 'totp.verify'
  | 'email.noVerify'
  | 'sms.noVerify'
  | 'totp.noVerify'
  | 'passkey.retry'
  | 'passkey.completed'
  | 'passkey.verified'
  | 'passkey.unavailable'
  | 'reset'
  | 'checkDone'
  | 'confirm';

interface Events {
  type: EventType;
  mfaScope?: MfaScope;
  passkeyAvailable?: boolean;
  authToken?: string;
  code?: string;
  email?: string;
  maskedPhoneNumber?: string;
  passwordResetToken?: string;
  withdrawalRequestId?: string;
}

type Emitted = { type: 'error'; error: AxiosError; channel: MfaVerifyChannel };

const initiateDoneAction = ({ event }: { event: DoneActorEvent<PostMfaInitiateResponse> }): Partial<Context> => {
  const { requireEmail, requirePasskey, requireSms, requireTotp } = event.output;

  return {
    ...event.output,
    needToVerify: [
      requireEmail && 'email',
      requirePasskey && 'passkey',
      requireSms && 'sms',
      requireTotp && 'totp',
    ].filter(Boolean),
  };
};

const hasInitiateRequirement = ({
  requireEmail,
  requirePasskey,
  requireSms,
  requireTotp,
}: {
  requirePasskey: boolean;
  requireTotp: boolean;
  requireSms: boolean;
  requireEmail: boolean;
}) => requireEmail || requirePasskey || requireSms || requireTotp;

const requestDoneAction = ({
  context,
  event,
  channel: channelName,
}: {
  context: Context;
  event: DoneActorEvent<PostMfaRequestResponse>;
  channel: MfaRequestChannel;
}): Partial<Context> => {
  const { channel, mfaSessionExpiresAt } = context;
  const { channelTokenDurationSec, channelTokenExpiresAt } = event.output;

  const minExpiredAt = min([
    add(new Date(), { seconds: Number(channelTokenDurationSec) }),
    convertDate(channelTokenExpiresAt)!,
    convertDate(mfaSessionExpiresAt)!,
  ])
    .getTime()
    .toString();

  return {
    channel: { ...channel, [channelName]: { expiresAt: minExpiredAt, verified: false } },
  };
};

const verifyDoneAction = ({ context, channel: channelName }: { context: Context; channel: MfaVerifyChannel }) => ({
  channel: { ...context.channel, [channelName]: { expiresAt: undefined, verified: true } },
  needToVerify: context.needToVerify.filter((channel) => channel !== channelName),
});

const initialContext: Partial<Context> = {
  mfaScope: undefined,
  passkeyAvailable: undefined,
  authToken: undefined,
  email: undefined,
  maskedPhoneNumber: undefined,
  passwordResetToken: undefined,
  withdrawalRequestId: undefined,
  requirePasskey: undefined,
  requireTotp: undefined,
  requireSms: undefined,
  requireEmail: undefined,
  mfaSessionToken: undefined,
  mfaSessionExpiresAt: undefined,
  channel: { sms: undefined, email: undefined, totp: undefined },
  needToVerify: [],
  isChannelLoading: false,
};

const mfaVerifyMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    output: {} as Context,
    emitted: {} as Emitted,
  },
  actors: {
    initiateMfa,
    requestMfaCode,
    verifyMfaCode,
  },
  actions: {
    checkVerifyRequiredAction: enqueueActions(({ enqueue, context }, { channel }: { channel: MfaVerifyChannel }) => {
      if (!context.needToVerify.includes(channel)) {
        enqueue.raise({ type: `${channel}.noVerify` });
      }
    }),
  },
}).createMachine({
  id: 'mfaVerify',
  initial: 'IDLE',
  context: initialContext as Context,
  output: ({ context }) => context,
  on: {
    reset: { target: '.IDLE', actions: assign(() => initialContext) },
  },
  states: {
    IDLE: {
      on: {
        initiate: {
          target: 'INITIATING',
          actions: assign(({ event }) => ({
            mfaScope: event.mfaScope,
            authToken: event.authToken,
            passkeyAvailable: event.passkeyAvailable,
            email: event.email,
            maskedPhoneNumber: event.maskedPhoneNumber,
            passwordResetToken: event.passwordResetToken,
            withdrawalRequestId: event.withdrawalRequestId,
          })),
        },
      },
    },
    INITIATING: {
      invoke: {
        src: 'initiateMfa',
        input: ({ context: { mfaScope, passkeyAvailable, authToken, email, passwordResetToken } }) => ({
          mfaScope,
          passkeyAvailable,
          authToken,
          email,
          passwordResetToken,
        }),
        onDone: [
          {
            target: 'PASSKEY',
            actions: assign(initiateDoneAction),
            guard: ({ event }) => event.output.requirePasskey,
          },
          {
            target: 'VERIFYING',
            actions: assign(initiateDoneAction),
            guard: ({ event }) => hasInitiateRequirement(event.output),
          },
          {
            target: 'NO_REQUIRED',
            actions: assign(initiateDoneAction),
            guard: ({ event }) => !hasInitiateRequirement(event.output),
          },
        ],
        onError: [
          { target: 'REQUIRE_MFA', guard: ({ event }) => getErrorCode(event.error) === 'api.mfa.noAvailableChannels' },
        ],
      },
    },
    PASSKEY: {
      on: {
        'passkey.retry': { target: 'PASSKEY', reenter: true },
        'passkey.unavailable': {
          target: 'INITIATING',
          actions: assign({ passkeyAvailable: false }),
        },
        'passkey.verified': {
          target: 'VERIFYING',
          guard: ({ context }) => context.needToVerify.filter((channel) => channel !== 'passkey').length > 0,
          actions: assign({
            needToVerify: ({ context }) => context.needToVerify.filter((channel) => channel !== 'passkey'),
          }),
        },
      },
    },
    VERIFYING: {
      type: 'parallel',
      states: {
        EMAIL: {
          initial: 'IDLE',
          states: {
            IDLE: {
              entry: { type: 'checkVerifyRequiredAction', params: { channel: 'email' } },
              on: { 'email.request': 'SEND', 'email.noVerify': 'NONE' },
            },
            SEND: {
              entry: assign({ isChannelLoading: true }),
              exit: assign({ isChannelLoading: false }),
              invoke: {
                src: 'requestMfaCode',
                input: ({ context }) => ({
                  channel: 'email',
                  mfaScope: context.mfaScope,
                  mfaSessionToken: context.mfaSessionToken,
                  authToken: context.authToken,
                  resetChannel: true,
                  email: context.email,
                  passwordResetToken: context.passwordResetToken,
                  context: { withdrawalRequestId: context.withdrawalRequestId },
                }),
                onDone: {
                  target: 'WAIT',
                  actions: assign(({ context, event }) => requestDoneAction({ context, event, channel: 'email' })),
                },
                onError: {
                  target: 'IDLE',
                  actions: emit(({ event }) => ({ type: 'error', channel: 'email', error: event.error as AxiosError })),
                },
              },
            },
            WAIT: { on: { 'email.request': 'SEND', 'email.verify': 'VERIFY' } },
            VERIFY: {
              entry: assign({ isChannelLoading: true }),
              exit: assign({ isChannelLoading: false }),
              invoke: {
                src: 'verifyMfaCode',
                input: ({ context, event }) => ({
                  mfaScope: context.mfaScope,
                  mfaSessionToken: context.mfaSessionToken,
                  authToken: context.authToken,
                  emailToken: event.code,
                  email: context.email,
                  passwordResetToken: context.passwordResetToken,
                }),
                onDone: {
                  target: 'VERIFIED',
                  actions: assign(({ context }) => verifyDoneAction({ context, channel: 'email' })),
                },
                onError: {
                  target: 'WAIT',
                  actions: emit(({ event }) => ({ type: 'error', channel: 'email', error: event.error as AxiosError })),
                },
              },
            },
            VERIFIED: { type: 'final' },
            NONE: { type: 'final' },
          },
        },
        SMS: {
          initial: 'IDLE',
          states: {
            IDLE: {
              entry: { type: 'checkVerifyRequiredAction', params: { channel: 'sms' } },
              on: { 'sms.request': 'SEND', 'sms.noVerify': 'NONE' },
            },
            SEND: {
              entry: assign({ isChannelLoading: true }),
              exit: assign({ isChannelLoading: false }),
              invoke: {
                src: 'requestMfaCode',
                input: ({ context }) => ({
                  channel: 'sms',
                  mfaScope: context.mfaScope,
                  mfaSessionToken: context.mfaSessionToken,
                  authToken: context.authToken,
                  resetChannel: true,
                  email: context.email,
                  passwordResetToken: context.passwordResetToken,
                  context: { withdrawalRequestId: context.withdrawalRequestId },
                }),
                onDone: {
                  target: 'WAIT',
                  actions: assign(({ context, event }) => requestDoneAction({ context, event, channel: 'sms' })),
                },
                onError: {
                  target: 'IDLE',
                  actions: emit(({ event }) => ({ type: 'error', channel: 'sms', error: event.error as AxiosError })),
                },
              },
            },
            WAIT: { on: { 'sms.request': 'SEND', 'sms.verify': 'VERIFY' } },
            VERIFY: {
              entry: assign({ isChannelLoading: true }),
              exit: assign({ isChannelLoading: false }),
              invoke: {
                src: 'verifyMfaCode',
                input: ({ context, event }) => ({
                  mfaScope: context.mfaScope,
                  mfaSessionToken: context.mfaSessionToken,
                  authToken: context.authToken,
                  smsToken: event.code,
                  email: context.email,
                  passwordResetToken: context.passwordResetToken,
                }),
                onDone: {
                  target: 'VERIFIED',
                  actions: assign(({ context }) => verifyDoneAction({ context, channel: 'sms' })),
                },
                onError: {
                  target: 'WAIT',
                  actions: emit(({ event }) => ({ type: 'error', channel: 'sms', error: event.error as AxiosError })),
                },
              },
            },
            VERIFIED: { type: 'final' },
            NONE: { type: 'final' },
          },
        },
        TOTP: {
          initial: 'IDLE',
          states: {
            IDLE: {
              entry: { type: 'checkVerifyRequiredAction', params: { channel: 'totp' } },
              on: { 'totp.verify': 'VERIFY', 'totp.noVerify': 'NONE' },
            },
            VERIFY: {
              entry: assign({ isChannelLoading: true }),
              exit: assign({ isChannelLoading: false }),
              invoke: {
                src: 'verifyMfaCode',
                input: ({ context, event }) => ({
                  mfaScope: context.mfaScope,
                  mfaSessionToken: context.mfaSessionToken,
                  authToken: context.authToken,
                  totpToken: event.code,
                  email: context.email,
                  passwordResetToken: context.passwordResetToken,
                }),
                onDone: {
                  target: 'VERIFIED',
                  actions: assign(({ context }) => verifyDoneAction({ context, channel: 'totp' })),
                },
                onError: {
                  target: 'IDLE',
                  actions: emit(({ event }) => ({ type: 'error', channel: 'totp', error: event.error as AxiosError })),
                },
              },
            },
            VERIFIED: { type: 'final' },
            NONE: { type: 'final' },
          },
        },
      },
      onDone: { target: 'WAIT_FOR_DONE' },
    },
    WAIT_FOR_DONE: {
      on: { confirm: 'DONE' },
    },
    NO_REQUIRED: { type: 'final' },
    REQUIRE_MFA: { type: 'final' },
    DONE: { type: 'final' },
  },
});

export default mfaVerifyMachine;
